import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  DEFAULT_REGISTRATION_COUNTRY,
  listRegistrationCountiesForState,
  listRegistrationStatesForCountry,
} from '@buxus/shared'
import { RegPasswordField } from '../ui/RegPasswordField'
import { RegReqLabel } from '../ui/RegReqLabel'
import {
  claimExistingScenarioAccount,
  readScenarioInviteToken,
  registerScenarioAccount,
  signedInEmail,
  signInForScenarioTenant,
} from '../scenarios/api/scenarioRegistration'
import { useAbp } from '../platform/abp/react'
import { rememberPostLoginStudioPath } from '../scenarios/serverMode'
import { detectRegistrationHostKind, RegistrationShell } from './registrationShell'
import { leaveRegistrationHost } from './registrationHostBridge'
import { showAppErrorModal, subscribeAppErrorModal } from '../platform/ui/appErrorModal'
import { hasNativeHostBridge } from '../platform/shell/nativeHostBridge'
import './RegistrationFlow.css'

const BANNER = 'Register'

type EndedInvite = { title: string; body: string; signOut?: boolean }

/** Same rules and copy as Express and standard registration. */
function passwordRequirement(password: string): string | undefined {
  if (password.length < 8) return 'Password must be at least 8 characters'
  if (password.length > 128) return 'Password must be 128 characters or fewer'
  if (!/[A-Z]/.test(password) || !/[^A-Za-z0-9]/.test(password) || !/[0-9]/.test(password)) {
    return 'Use at least 8 characters, including one uppercase letter, one number, and one special character (such as !, @, or #).'
  }
  return undefined
}

function describeEndedInvite(raw: string): EndedInvite | null {
  if (/no longer on the session/i.test(raw)) {
    return { title: 'This link is no longer valid', body: '' }
  }
  if (/not valid|has expired|expired/i.test(raw)) {
    return {
      title: 'This invitation link is not valid',
      body: 'This Scenario Studio invitation link cannot be used. Ask the person who invited you to send a new one.',
    }
  }
  if (/more than one account|already has an account/i.test(raw)) {
    return {
      title: 'Sign in with your Buxus account',
      body: 'That email already belongs to a Buxus account. Sign in with the one you already use. A new account was not created.',
    }
  }
  return null
}

export function ScenarioRegistrationPage() {
  const hostKind = detectRegistrationHostKind()
  const navigate = useNavigate()
  const { logout } = useAbp()
  const [params] = useSearchParams()
  const token = params.get('token')?.trim() ?? ''
  const invite = useMemo(() => (token ? readScenarioInviteToken(token) : null), [token])
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [stateName, setStateName] = useState('')
  const [county, setCounty] = useState('')
  const [organizationName, setOrganizationName] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [serverPasswordError, setServerPasswordError] = useState<string | null>(null)
  const [formLocked, setFormLocked] = useState(false)
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [gate, setGate] = useState<'checking' | 'ready'>('checking')
  const [ended, setEnded] = useState<EndedInvite | null>(null)

  function leave() {
    leaveRegistrationHost(navigate)
  }

  const stateOptions = useMemo(
    () => listRegistrationStatesForCountry(DEFAULT_REGISTRATION_COUNTRY),
    [],
  )
  const countyOptions = useMemo(
    () => listRegistrationCountiesForState(DEFAULT_REGISTRATION_COUNTRY, stateName),
    [stateName],
  )

  useEffect(() => {
    return subscribeAppErrorModal((req) => {
      if (!req) setFormLocked(false)
    })
  }, [])

  function presentFailure(raw: string) {
    const passwordRule = /passwords? must|password.*(character|digit|uppercase|lowercase|special|alphanumeric)/i.test(
      raw,
    )
    if (passwordRule) {
      setServerPasswordError(raw)
      setSubmitError(null)
      return
    }
    setSubmitError(null)
    setServerPasswordError(null)
    if (!hasNativeHostBridge()) setFormLocked(true)
    showAppErrorModal({ message: raw })
  }

  useEffect(() => {
    if (!invite || !token) return
    let cancelled = false
    void claimExistingScenarioAccount(token)
      .then(async (result) => {
        if (cancelled) return
        if (result.accountExists && result.sessionId) {
          const current = (await signedInEmail()).trim()
          const invited = invite.email.trim()
          if (current && current.toLowerCase() !== invited.toLowerCase()) {
            setEnded({
              title: `This invitation is for ${invited}`,
              body: `You're signed in as ${current}. To accept the invitation, sign out, then open the link again and sign in as ${invited}.`,
              signOut: true,
            })
            return
          }
          if (!current) rememberPostLoginStudioPath(result.sessionId)
          window.location.assign(`/scenarios/${result.sessionId}`)
          return
        }
        setGate('ready')
      })
      .catch((error) => {
        if (cancelled) return
        const raw = error instanceof Error ? error.message : ''
        const closed = describeEndedInvite(raw)
        if (closed) {
          setEnded(closed)
          return
        }
        presentFailure(raw || 'Could not check this invitation. Please try again.')
        setGate('ready')
      })
    return () => {
      cancelled = true
    }
  }, [invite, token])

  async function handleSubmit() {
    if (!invite || !token || isSubmitting || formLocked) return
    setSubmitAttempted(true)
    setSubmitError(null)
    setServerPasswordError(null)
    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !organizationName.trim() ||
      !stateName ||
      !county ||
      passwordRequirement(password) ||
      !passwordConfirm ||
      password !== passwordConfirm
    ) {
      return
    }
    setIsSubmitting(true)
    try {
      const result = await registerScenarioAccount({
        token,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
        state: stateName,
        county,
        organizationName: organizationName.trim(),
      })
      if (result.signedIn && result.tenantId) {
        try {
          await signInForScenarioTenant(result.email || invite.email, password, result.tenantId)
        } catch (error) {
          const message = error instanceof Error ? error.message : ''
          presentFailure(
            message || 'Your account was created. Sign in with the password you just chose.',
          )
          setIsSubmitting(false)
          return
        }
      }
      window.location.assign(`/scenarios/${result.sessionId}`)
    } catch (error) {
      const raw =
        error instanceof Error && error.message
          ? error.message
          : 'Your registration could not be completed. Please try again.'
      const closed = describeEndedInvite(raw)
      if (closed) {
        setEnded(closed)
        setIsSubmitting(false)
        return
      }
      presentFailure(raw)
      setIsSubmitting(false)
    }
  }

  function fieldMessage(invalid: boolean, message: string): string | undefined {
    if (!submitAttempted || !invalid) return undefined
    return message
  }

  if (!invite || ended) {
    const closed = ended ?? {
      title: 'This invitation link is not valid',
      body: 'This Scenario Studio invitation link cannot be used. Ask the person who invited you to send a new one.',
    }
    return (
      <RegistrationShell hostKind={hostKind} title={BANNER}>
        <section className="reg-card">
          <h2 className="reg-step-title">{closed.title}</h2>
          {closed.body ? <p className="reg-step-lead">{closed.body}</p> : null}
          <div className="reg-actions">
            {closed.signOut ? (
              <button type="button" className="reg-btn primary" onClick={() => void logout()}>
                Sign out
              </button>
            ) : (
              <button type="button" className="reg-btn primary" onClick={leave}>
                Back to Buxus
              </button>
            )}
          </div>
        </section>
      </RegistrationShell>
    )
  }

  if (gate !== 'ready') {
    return (
      <RegistrationShell hostKind={hostKind} title={BANNER}>
        <div className="reg-loading" role="status" aria-live="polite" aria-busy="true">
          <span className="reg-loading-mark" />
          <span className="reg-sr">Loading</span>
        </div>
      </RegistrationShell>
    )
  }

  const firstNameError = fieldMessage(!firstName.trim(), 'First name is required')
  const lastNameError = fieldMessage(!lastName.trim(), 'Last name is required')
  const organizationError = fieldMessage(!organizationName.trim(), 'Organization name is required')
  const stateError = fieldMessage(!stateName, 'State is required')
  const countyError = fieldMessage(!county, 'County is required')
  const passwordProblem = passwordRequirement(password)
  const passwordError = (submitAttempted ? passwordProblem : undefined) ?? serverPasswordError ?? undefined
  const passwordConfirmError = fieldMessage(
    !passwordConfirm || password !== passwordConfirm,
    !passwordConfirm ? 'Please confirm your password' : 'Password does not match',
  )

  return (
    <RegistrationShell hostKind={hostKind} title={BANNER}>
      <section className={`reg-card${isSubmitting ? ' is-submitting' : ''}`}>
        {isSubmitting ? (
          <div className="reg-card-busy" role="status" aria-live="polite" aria-busy="true">
            <span className="reg-loading-mark" />
            <span className="reg-sr">Creating account</span>
          </div>
        ) : null}
        <h2 className="reg-step-title">Scenario Studio</h2>
        <p className="reg-step-lead">
          You&apos;ve been invited to a Scenario Studio session. Create your Buxus account here to join
          it. When you finish, you&apos;ll be taken straight to the session.
        </p>
        {submitError ? <p className="reg-error">{submitError}</p> : null}
        <fieldset className="reg-fields" disabled={isSubmitting || formLocked}>
        <div className="reg-grid">
          <label className="reg-field">
            <RegReqLabel required>First Name</RegReqLabel>
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
            />
            {firstNameError ? <span className="reg-field-error">{firstNameError}</span> : null}
          </label>
          <label className="reg-field">
            <RegReqLabel required>Last Name</RegReqLabel>
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
            />
            {lastNameError ? <span className="reg-field-error">{lastNameError}</span> : null}
          </label>
          <label className="reg-field reg-field--full">
            <RegReqLabel>Email</RegReqLabel>
            <input value={invite.email} disabled autoComplete="off" data-1p-ignore data-lpignore="true" />
          </label>
          <label className="reg-field reg-field--full">
            <RegReqLabel required>Organization Name</RegReqLabel>
            <input
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              autoComplete="organization"
            />
            {organizationError ? <span className="reg-field-error">{organizationError}</span> : null}
          </label>
          <label className="reg-field">
            <RegReqLabel required>State</RegReqLabel>
            <select
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              value={stateName}
              onChange={(e) => {
                setStateName(e.target.value)
                setCounty('')
              }}
            >
              <option value="">Select</option>
              {stateOptions.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
            {stateError ? <span className="reg-field-error">{stateError}</span> : null}
          </label>
          <label className="reg-field">
            <RegReqLabel required>County</RegReqLabel>
            <select
              value={county}
              disabled={!stateName || isSubmitting}
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              onChange={(e) => setCounty(e.target.value)}
            >
              <option value="">Select</option>
              {countyOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            {countyError ? <span className="reg-field-error">{countyError}</span> : null}
          </label>
          <RegPasswordField
            label="Password"
            required
            value={password}
            onChange={(value) => {
              setPassword(value)
              setServerPasswordError(null)
            }}
            autoComplete="new-password"
            error={passwordError}
          />
          <RegPasswordField
            label="Password Confirm"
            required
            value={passwordConfirm}
            matchesValue={password}
            onChange={setPasswordConfirm}
            autoComplete="new-password"
            error={passwordConfirmError}
          />
        </div>
        <div className="reg-actions">
          <button
            type="button"
            className="reg-btn primary"
            disabled={isSubmitting}
            onClick={() => void handleSubmit()}
          >
            {isSubmitting ? 'Creating account…' : 'Create account and join'}
          </button>
        </div>
        </fieldset>
      </section>
    </RegistrationShell>
  )
}
